<template>
  <div class="library">
    <section ref="hero" class="hero">
      <div class="hero-default">
        <img
          class="pill-graphic"
          src="../assets/library-capsule.svg"
          alt=""
          draggable="false"
        />
        <h1>프로젝트에 꼭 맞는 일러스트 아티스트를 찾아 처방해드려요.</h1>
      </div>
      <div class="hero-hover" aria-hidden="true">
        <h2>프로젝트에 꼭 맞는 아티스트, 찾고 계신가요?</h2>
        <p>
          천천히 둘러보세요.<br />
          프로젝트와 닮은 아티스트를 발견하는 순간,<br />
          그 다음은 저희가 함께할게요.
        </p>
      </div>
    </section>

    <div class="results-area">
      <div class="capsule-peek" aria-hidden="true"></div>
      <div class="filter-controls">
        <div class="filter-bar">
          <button
            type="button"
            class="filter-btn"
            :class="{ open: filterOpen }"
            :aria-expanded="String(filterOpen)"
            @click="filterOpen = !filterOpen"
          >
            <svg class="filter-icon" viewBox="0 0 32 24" aria-hidden="true">
              <path d="M2 5H30 M2 12H30 M2 19H30" />
              <circle cx="23" cy="5" r="3" />
              <circle cx="14" cy="12" r="3" />
              <circle cx="8" cy="19" r="3" />
            </svg>
            Filter
            <span class="results-count">({{ filtered.length }} Results)</span>
          </button>
          <div class="search-box">
            <input
              v-model.trim="query"
              type="search"
              placeholder="라이브러리 아티스트 검색"
              aria-label="라이브러리 아티스트 검색"
            />
            <span class="search-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="#111"
                stroke-width="2"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
          </div>
        </div>

        <div class="filter-panel" v-show="filterOpen">
          <h2>Search by Category</h2>
          <div v-for="(group, index) in filterGroups" :key="group.key">
            <div class="filter-group-heading">
              <h3>{{ group.label }}</h3>
              <button
                type="button"
                class="reset"
                aria-label="선택 초기화"
                :disabled="!selected[group.key].length"
                @click="clearGroup(group.key)"
              >
                ↻
              </button>
            </div>
            <div
              class="tag-row"
              :class="{ 'no-border': index === filterGroups.length - 1 }"
            >
              <button
                type="button"
                class="tag"
                v-for="option in group.options"
                :key="option"
                :class="{ active: selected[group.key].includes(option) }"
                @click="toggle(group.key, option)"
              >
                {{ option }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="grid-wrap">
        <div class="grid" v-if="loading">
          <div class="artist-card skeleton" v-for="n in 8" :key="n">
            <div class="card-image"></div>
            <div class="card-name">&nbsp;</div>
          </div>
        </div>
        <p class="status" v-else-if="error">
          아티스트 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
        </p>
        <p class="status" v-else-if="!filtered.length">
          조건에 맞는 아티스트가 없습니다.<br />
          찾는 스타일이 없다면,
          <router-link to="/about/contact/"
            >큐레이션을 요청하세요 →</router-link
          >
        </p>
        <div class="grid" v-else>
          <button
            type="button"
            class="artist-card"
            v-for="artist in filtered"
            :key="artist.id"
            @click="open(artist)"
            @mouseenter="prefetch(artist, 0)"
          >
            <div class="card-image">
              <img
                :src="artist.images[0].large"
                :alt="artist.name"
                loading="lazy"
                v-if="artist.images.length"
              />
            </div>
            <div class="card-name">{{ artist.name }}</div>
          </button>
        </div>
      </div>
    </div>

    <b-modal
      v-model="modalOpen"
      hide-header
      hide-footer
      centered
      dialog-class="library-dialog"
      content-class="library-modal"
      body-class="p-0"
    >
      <div class="artist-modal" v-if="current">
        <button
          type="button"
          class="modal-close"
          aria-label="소개 팝업 닫기"
          @click="modalOpen = false"
        >
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path d="M4 4 L16 16 M16 4 L4 16" />
          </svg>
        </button>
        <div class="modal-surface">
          <div
            class="modal-art"
            :class="{ 'is-single': current.images.length < 2 }"
          >
            <div
              class="modal-slide-track"
              :style="{ transform: `translateX(-${slideIndex * 100}%)` }"
            >
              <div
                class="modal-slide"
                v-for="(image, index) in current.images"
                :key="image.id"
              >
                <img
                  class="modal-slide-image"
                  :src="image.large"
                  :alt="current.name"
                />
                <img
                  class="modal-slide-image modal-slide-full"
                  :class="{ 'is-loaded': loadedFull[image.id] }"
                  :src="image.full"
                  alt=""
                  decoding="async"
                  @load="$set(loadedFull, image.id, true)"
                  v-if="Math.abs(index - slideIndex) <= 1"
                />
              </div>
            </div>
            <button
              type="button"
              class="modal-slide-arrow modal-slide-prev"
              aria-label="이전 작품"
              @click="slide(-1)"
            >
              ‹
            </button>
            <button
              type="button"
              class="modal-slide-arrow modal-slide-next"
              aria-label="다음 작품"
              @click="slide(1)"
            >
              ›
            </button>
            <div class="modal-slide-dots" aria-label="작품 이미지 선택">
              <button
                type="button"
                v-for="(image, index) in current.images"
                :key="image.id"
                :class="{ 'is-active': index === slideIndex }"
                :aria-label="`${index + 1}번 작품`"
                @click="slideIndex = index"
              ></button>
            </div>
          </div>

          <h2>{{ current.name }}</h2>

          <div class="modal-info">
            <h3>ARTIST Category &amp; Specialty</h3>
            <div class="modal-tags">
              <span
                v-for="tag in current.category.concat(current.specialty)"
                :key="tag"
              >
                {{ tag }}
              </span>
            </div>
          </div>

          <div class="modal-actions">
            <router-link class="primary-action" to="/about/contact/">
              이 아티스트와 함께하기 <span aria-hidden="true">→</span>
            </router-link>
          </div>
        </div>
      </div>
    </b-modal>

    <router-link
      ref="cta"
      to="/about/contact/"
      class="floating-cta"
      :class="{ 'is-visible': ctaVisible }"
      :aria-hidden="String(!ctaVisible)"
      :tabindex="ctaVisible ? null : -1"
      aria-label="프로젝트 문의하기"
    >
      <span class="floating-cta-stage" aria-hidden="true">
        <span class="floating-cta-face floating-cta-front">
          <img src="../assets/library-cta-front.svg" alt="" />
        </span>
        <span class="floating-cta-face floating-cta-back">
          <img src="../assets/library-cta-back.svg" alt="" />
        </span>
      </span>
    </router-link>
  </div>
</template>

<script>
import axios from "axios";

const FIELDS = {
  name: "Name",
  instagram: "Assignee",
  attachments: "Attachments",
  category: "분야1_AI",
  specialty: "분야2_AI"
};

const EMOJI = /[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}️]/gu;

function splitTags(value) {
  return String(value || "")
    .split(",")
    .map(tag => tag.replace(EMOJI, "").trim())
    .filter(Boolean);
}

function thumbnail(attachment, size) {
  const thumbnails = attachment.thumbnails || {};
  return (thumbnails[size] && thumbnails[size].url) || attachment.url;
}

function normalize(record) {
  return {
    id: record.id,
    name: record[FIELDS.name] || "이름 없음",
    instagram: record[FIELDS.instagram] || "",
    images: (record[FIELDS.attachments] || [])
      .filter(attachment => attachment.url)
      .map(attachment => ({
        id: attachment.id,
        large: thumbnail(attachment, "large"),
        full: thumbnail(attachment, "full")
      })),
    category: splitTags(record[FIELDS.category]),
    specialty: splitTags(record[FIELDS.specialty])
  };
}

function uniqueSorted(values) {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b, "ko"));
}

export default {
  name: "Library",
  data() {
    return {
      artists: [],
      loading: true,
      error: false,
      query: "",
      filterOpen: false,
      selected: { category: [], specialty: [] },
      modalOpen: false,
      current: null,
      slideIndex: 0,
      loadedFull: {},
      ctaVisible: false,
      ctaTicking: false
    };
  },
  computed: {
    filterGroups() {
      return [
        {
          key: "category",
          label: "Category",
          options: uniqueSorted(this.artists.flatMap(a => a.category))
        },
        {
          key: "specialty",
          label: "Specialty/Discipline",
          options: uniqueSorted(this.artists.flatMap(a => a.specialty))
        }
      ];
    },
    filtered() {
      const query = this.query.toLowerCase();
      return this.artists.filter(artist => {
        if (query && !artist.name.toLowerCase().includes(query)) {
          return false;
        }
        return Object.keys(this.selected).every(key => {
          const wanted = this.selected[key];
          return !wanted.length || artist[key].some(t => wanted.includes(t));
        });
      });
    }
  },
  async created() {
    try {
      const response = await axios.get("/api/v1/airtable/artists/");
      this.artists = response.data.map(normalize);
    } catch (e) {
      this.error = true;
    } finally {
      this.loading = false;
    }
  },
  mounted() {
    window.addEventListener("scroll", this.requestCtaUpdate, { passive: true });
    window.addEventListener("resize", this.requestCtaUpdate);
    this.updateCta();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.requestCtaUpdate);
    window.removeEventListener("resize", this.requestCtaUpdate);
  },
  methods: {
    // The floating contact button shows once the hero has scrolled clear of it.
    updateCta() {
      this.ctaTicking = false;
      const { hero, cta } = this.$refs;
      if (!hero || !cta) {
        return;
      }
      const ctaTop = window.innerHeight - 30 - cta.$el.offsetHeight;
      this.ctaVisible = hero.getBoundingClientRect().bottom < ctaTop - 20;
    },
    requestCtaUpdate() {
      if (!this.ctaTicking) {
        this.ctaTicking = true;
        requestAnimationFrame(this.updateCta);
      }
    },
    toggle(key, option) {
      const list = this.selected[key];
      const index = list.indexOf(option);
      if (index === -1) {
        list.push(option);
      } else {
        list.splice(index, 1);
      }
    },
    clearGroup(key) {
      this.selected[key] = [];
    },
    open(artist) {
      this.current = artist;
      this.slideIndex = 0;
      this.loadedFull = {};
      this.modalOpen = true;
    },
    slide(step) {
      const total = this.current.images.length;
      this.slideIndex = (this.slideIndex + step + total) % total;
    },
    // Warm the browser cache for a full-size image before it is shown.
    prefetch(artist, index) {
      const image = artist.images[index];
      if (image) {
        new Image().src = image.full;
      }
    }
  }
};
</script>

<!-- Modal is rendered outside the component root, so its styles are global. -->
<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap");
@import url("https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable.min.css");

.modal-dialog.library-dialog {
  max-width: 720px;

  .library-modal {
    background: transparent;
    border: 0;
    box-shadow: none;
    border-radius: 0;
  }
}

.artist-modal {
  position: relative;
  font-family: "Pretendard Variable", Pretendard, -apple-system, sans-serif;
  color: #111111;

  /* Sits on the surface's semicircular rim at 45deg: the rim radius is half
     the width (360px at 720px), so the point is 360 * (1 - cos45) ~= 105px in
     from the top and right; minus half the button size. */
  .modal-close {
    position: absolute;
    top: 83px;
    right: 83px;
    z-index: 3;
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 2px solid #111111;
    border-radius: 50%;
    background: #ffffff;
    color: #111111;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease,
      transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);

    svg {
      width: 18px;
      height: 18px;
      fill: none;
      stroke: currentColor;
      stroke-width: 2.2;
      stroke-linecap: round;
    }

    &:focus {
      outline: none;
    }
    &:hover,
    &:focus-visible {
      background: #111111;
      color: #ffffff;
    }
    &:focus-visible {
      box-shadow: 0 0 0 3px rgba(17, 17, 17, 0.25);
    }

    &:active {
      transform: scale(0.94);
    }
  }

  .modal-surface {
    width: 100%;
    max-height: 92vh;
    /* Top padding equals the side padding so the circular artwork is
       concentric with the rounded top of the surface (even ring width). */
    padding: 32px 32px 56px;
    overflow: auto;
    scrollbar-width: none;
    border-radius: 999px 999px 42px 42px;
    background: #ffffff;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.3);

    &::-webkit-scrollbar {
      display: none;
    }

    > h2 {
      margin: 0 0 30px;
      font-size: 27px;
      font-weight: 800;
    }
  }

  .modal-art {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    margin: 0 auto 22px;
    border-radius: 50%;
    background-color: #111111;
    overflow: hidden;

    &.is-single .modal-slide-arrow,
    &.is-single .modal-slide-dots {
      display: none;
    }
  }

  .modal-slide-track {
    display: flex;
    width: 100%;
    height: 100%;
    transition: transform 0.68s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .modal-slide {
    position: relative;
    flex: 0 0 100%;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .modal-slide-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transform: scale(1.08);
  }

  /* The cached `large` thumbnail shows immediately; the full-size image
     fades in on top once it has loaded. */
  .modal-slide-full {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    transition: opacity 0.35s ease;

    &.is-loaded {
      opacity: 1;
    }
  }

  .modal-slide-arrow {
    position: absolute;
    top: 50%;
    z-index: 2;
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    padding: 0 0 4px;
    border: 0;
    border-radius: 50%;
    color: #ffffff;
    background: rgba(0, 0, 0, 0.42);
    font-size: 38px;
    line-height: 1;
    cursor: pointer;
    transform: translateY(-50%);
    transition: background-color 0.2s ease;

    /* Bootstrap's `outline: auto` focus ring ignores border-radius; use a
       shadow ring that follows the circle instead. */
    &:focus {
      outline: none;
    }
    &:focus-visible {
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.85);
    }
    &:hover {
      background: rgba(0, 0, 0, 0.6);
    }
  }
  .modal-slide-prev {
    left: 16px;
  }
  .modal-slide-next {
    right: 16px;
  }

  .modal-slide-dots {
    position: absolute;
    right: 0;
    bottom: 16px;
    left: 0;
    z-index: 2;
    display: flex;
    justify-content: center;
    gap: 7px;

    button {
      width: 8px;
      height: 8px;
      padding: 0;
      border: 1px solid #ffffff;
      border-radius: 50%;
      background: transparent;
      cursor: pointer;

      &:focus {
        outline: none;
      }
      &:focus-visible {
        box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.6);
      }
      &.is-active {
        background: #ffffff;
      }
    }
  }

  .modal-info {
    text-align: left;

    h3 {
      margin: 0;
      padding-bottom: 12px;
      border-bottom: 1.5px solid #111111;
      font-size: 15px;
      font-weight: 700;
    }
  }

  .modal-tags {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px 0;
    padding: 24px 0 28px;
    border-bottom: 1.5px solid #111111;

    span {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: calc(25% + 16.5px);
      min-height: 42px;
      margin-left: -22px;
      padding: 0 18px;
      border: 1.5px solid #111111;
      border-radius: 999px;
      background: #ffffff;
      text-align: center;
      font-size: 12.5px;
      font-weight: 700;
      line-height: 1.15;

      &:nth-child(4n + 1) {
        margin-left: 0;
      }
    }
  }

  .modal-actions {
    display: flex;
    justify-content: center;
    margin-top: 36px;
  }

  .primary-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 360px;
    min-height: 56px;
    padding: 16px 28px;
    border-radius: 999px;
    background: #b10101;
    color: #ffffff;
    font-size: 15px;
    font-weight: 800;
    line-height: 1;

    span {
      display: inline-block;
      margin-left: 7px;
      transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
    }

    &:hover {
      color: #ffffff;
      text-decoration: none;
      background: #c40d14;

      span {
        transform: translateX(4px);
      }
    }
  }

  @media (max-width: 760px) {
    .modal-close {
      /* rim radius ~170px on a ~340px-wide sheet -> 45deg point ~50px in */
      top: 31px;
      right: 31px;
      width: 38px;
      height: 38px;

      svg {
        width: 16px;
        height: 16px;
      }
    }
    .modal-surface {
      max-height: 85vh;
      padding: 18px 18px 22px;
      border-radius: 999px 999px 24px 24px;

      > h2 {
        font-size: 22px;
      }
    }
    .modal-art {
      aspect-ratio: 1 / 1.08;
      border-radius: 999px 999px 0 0;
      margin-bottom: 20px;
    }
    .modal-tags span {
      width: calc(50% + 11px);
      min-height: 38px;
      padding: 0 10px;
      font-size: 12px;

      &:nth-child(4n + 1) {
        margin-left: -22px;
      }
      &:nth-child(odd) {
        margin-left: 0;
      }
    }
    .modal-actions {
      margin-top: 26px;
    }
    .primary-action {
      width: 100%;
      min-height: 52px;
      font-size: 14px;
    }
  }
}
</style>

<style lang="scss" scoped>
$red: #b10101;
$black: #111111;
$white: #ffffff;
$edge: 20px;

.library {
  font-family: "Pretendard Variable", Pretendard, -apple-system, "Noto Sans KR",
    sans-serif;
  color: $black;
  background: $red;
  padding-bottom: 80px;

  button {
    font-family: inherit;
  }
}

/* ---------- hero ---------- */

.hero {
  position: relative;
  height: 85vh;
  min-height: 600px;
  overflow: hidden;
  text-align: center;
  background: $red;
  user-select: none;
  -webkit-user-select: none;
}

.hero-default,
.hero-hover {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  transition: opacity 0.55s ease, transform 0.65s cubic-bezier(0.22, 1, 0.36, 1);
}

.hero-default {
  opacity: 1;
  transform: translateY(0);

  h1 {
    color: $white;
    font-size: 30px;
    font-weight: 800;
    letter-spacing: -0.3px;
    word-break: keep-all;
    margin: 0;
  }
}

.hero-hover {
  color: $white;
  opacity: 0;
  transform: translateY(28px);
  pointer-events: none;

  h2 {
    margin: 0 0 24px;
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -0.5px;
  }
  p {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.65;
    letter-spacing: -0.3px;
  }
}

.hero:hover .hero-default {
  opacity: 0;
  transform: translateY(-24px);
}
.hero:hover .hero-hover {
  opacity: 1;
  transform: translateY(0);
}

.pill-graphic {
  display: block;
  width: 340px;
  max-width: 70vw;
  height: auto;
  margin: 0 auto 50px;
  pointer-events: none;
  -webkit-user-drag: none;
}

/* ---------- results area (white sheet) ---------- */
/* Mirrors the mockup: the filter bar overlaps the hero by 60px; a white
   "capsule" dome (.capsule-peek) starts right under the bar and a plain
   white fill (::after) continues below it. */

$overlap: 60px;
$filter-bar: 56px;

.results-area {
  position: relative;
  isolation: isolate;
  margin-top: -$overlap;
  padding-top: $overlap;
  min-height: 60vh;

  &::after {
    content: "";
    position: absolute;
    z-index: -2;
    right: 0;
    bottom: 0;
    left: 0;
    top: calc(#{$overlap + $filter-bar - 60px} + 100vh);
    background: $white;
  }
}

.capsule-peek {
  position: absolute;
  z-index: -1;
  right: 0;
  left: 0;
  top: $overlap + $filter-bar - 60px;
  height: 100vh;
  background: $white;
  border-radius: 50% 50% 0 0 / clamp(520px, 30vw, 1100px)
    clamp(520px, 30vw, 1100px) 0 0;
}

.filter-controls,
.grid-wrap {
  position: relative;
  z-index: 0;
}

.filter-controls {
  position: sticky;
  top: 60px;
  z-index: 20;
}

.filter-bar {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  gap: 2px;
  padding: 6px $edge;
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 52px;
  padding: 0 18px;
  border: 2px solid $black;
  border-radius: 0;
  background: $white;
  color: $black;
  font-family: "Poppins", "Pretendard Variable", Pretendard, sans-serif;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;

  .filter-icon {
    width: 28px;
    height: 22px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.5;
    stroke-linecap: round;
  }

  .results-count {
    align-self: center;
    margin-left: 4px;
    padding-top: 4px;
    font-size: 17px;
    font-weight: 700;
    line-height: 1;
    color: currentColor;
  }

  &::after {
    content: "";
    width: 0;
    height: 0;
    margin-left: auto;
    border-right: 10px solid transparent;
    border-left: 10px solid transparent;
    border-top: 14px solid currentColor;
    transition: transform 0.3s ease;
  }

  &.open {
    color: $white;
    background: $black;

    &::after {
      transform: rotate(180deg);
    }
  }
}

.search-box {
  display: flex;
  align-items: center;
  height: 52px;
  overflow: hidden;
  background: $white;
  border: 2px solid $black;

  input {
    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0 16px;
    border: none;
    outline: none;
    background: transparent;
    font-family: "Poppins", "Pretendard Variable", Pretendard, sans-serif;
    font-size: 18px;
  }

  .search-icon {
    display: flex;
    align-items: center;
    padding: 0 14px;
  }
}

.filter-panel {
  margin: 0 $edge 24px;
  padding: 26px $edge 30px;
  background: $white;
  border: 2px solid $black;

  h2 {
    margin: 0 0 18px;
    font-size: 17px;
    font-weight: 800;
  }

  .filter-group-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 13px;
    padding-bottom: 9px;
    border-bottom: 1.5px solid $black;

    h3 {
      margin: 0;
      font-size: 13px;
      font-weight: 800;
    }

    .reset {
      border: 0;
      background: none;
      font-size: 16px;
      line-height: 1;
      color: $black;
      cursor: pointer;

      &:disabled {
        color: #bbb;
        cursor: default;
      }
    }
  }

  .tag-row {
    display: grid;
    grid-template-columns: repeat(7, minmax(120px, 180px));
    gap: 10px 0;
    width: 100%;
    margin-bottom: 20px;

    &.no-border {
      margin-bottom: 0;
    }
  }

  .tag {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: calc(100% + 22px);
    min-height: 44px;
    margin-left: -22px;
    padding: 0 24px;
    border: 1.5px solid $black;
    border-radius: 999px;
    color: $black;
    background: $white;
    font-size: 12.5px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    transform: translateY(0) scale(1);
    transition: color 0.25s ease, background-color 0.3s ease,
      border-color 0.3s ease, transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 0.3s ease;

    &:nth-child(7n + 1) {
      width: 100%;
      margin-left: 0;
    }

    /* Same order as the mockup: .active, then hover, then :active last so a
       press always wins (also on an already-selected chip). Stacking order is
       left by DOM order only, so overlaps never change on hover/select. */
    &.active {
      color: $white;
      background: $black;
      border-color: $white;
      transform: translateY(-2px) scale(1.015);
      box-shadow: 0 5px 12px rgba(0, 0, 0, 0.14);
    }

    &:not(.active):hover,
    &:not(.active):focus-visible {
      transform: translateY(-1px);
      background: #f2f2f2;
    }

    &:focus {
      outline: none;
    }
    &:focus-visible {
      box-shadow: 0 0 0 3px rgba(17, 17, 17, 0.25);
    }

    &:active {
      transform: translateY(1px) scale(0.97);
    }
  }
}

/* ---------- grid ---------- */

.grid-wrap {
  margin: 0 $edge;
  padding-top: 8px;
}

.grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
}

.artist-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0;
  margin-bottom: 20px;
  border: 2px solid $black;
  border-radius: 0;
  background: $white;
  color: $black;
  text-align: left;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover,
  &:focus-visible {
    z-index: 2;
    transform: translateY(-6px);
    outline: none;
  }

  .card-image {
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 5;
    overflow: hidden;
    background: $white;

    img {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }

  .card-name {
    padding: 12px 10px;
    border-top: 2px solid $black;
    background: $white;
    font-size: 18px;
    font-weight: 700;
  }

  &.skeleton {
    cursor: default;

    .card-image {
      background: linear-gradient(90deg, #f1f1f1 25%, #e6e6e6 50%, #f1f1f1 75%);
      background-size: 200% 100%;
      animation: shimmer 1.2s infinite;
    }
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.status {
  text-align: center;
  color: #666;
  font-size: 15px;
  line-height: 1.7;
  margin: 40px 0 0;

  a {
    color: $red;
    font-weight: 700;
  }
}

/* ---------- responsive ---------- */

@media (max-width: 1100px) {
  .hero {
    height: calc(90vh - 60px);
    min-height: 500px;
  }
  .hero-default,
  .hero-hover {
    padding: 30px 20px 100px;
  }
  .hero-default h1 {
    max-width: 360px;
    font-size: clamp(16px, 4.3vw, 21px);
    line-height: 1.45;
  }
  .hero-hover h2 {
    font-size: 22px;
  }
  .hero-hover p {
    font-size: 15px;
  }
  .pill-graphic {
    width: 64vw;
    max-width: 300px;
    margin-bottom: 34px;
  }

  .filter-bar {
    display: flex;
    padding: 10px 10px 15px;
  }
  .search-box {
    display: none;
  }
  .filter-btn {
    justify-content: flex-start;
    padding: 12px 17px;

    &::after {
      border-right-width: 12px;
      border-left-width: 12px;
      border-top-width: 18px;
    }
  }

  .filter-panel {
    margin: 0 10px 15px;
    padding: 26px 20px 34px;

    > h2 {
      display: none;
    }
    .filter-group-heading {
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom-width: 2px;

      h3 {
        font-size: 20px;
      }
    }
    .tag-row {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 12px 0;
      margin-bottom: 32px;

      &.no-border {
        margin-bottom: 0;
      }
    }
    .tag {
      width: calc(100% + 8px);
      min-height: 42px;
      margin-left: -8px;
      padding: 0 2px;
      font-size: clamp(8.5px, 2.5vw, 15px);
      letter-spacing: -0.04em;

      &:nth-child(7n + 1) {
        width: calc(100% + 8px);
        margin-left: -8px;
      }
      &:nth-child(3n + 1) {
        z-index: 1;
        width: 100%;
        margin-left: 0;
      }
      &:nth-child(3n + 2) {
        z-index: 2;
      }
      &:nth-child(3n) {
        z-index: 3;
      }
    }
  }

  .capsule-peek {
    border-radius: 50% 50% 0 0 / 180px 180px 0 0;
  }

  .grid-wrap {
    margin: 0 10px;
    padding-bottom: 40px;
  }
  .grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 4px;
  }
  .artist-card {
    margin-bottom: 4px;

    .card-name {
      min-height: 35px;
      padding: 10px 8px;
      font-size: 14px;
    }
  }
}

@media (min-width: 761px) and (max-width: 1100px) {
  .hero {
    height: 78vh;
    min-height: 540px;
  }
  .hero-default,
  .hero-hover {
    padding: 44px 20px;
  }
  .pill-graphic {
    width: 300px;
    margin-bottom: 40px;
  }
  .grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .filter-panel .tag-row {
    grid-template-columns: repeat(5, minmax(120px, 160px));
  }
  .filter-panel .tag {
    &:nth-child(3n + 1) {
      width: calc(100% + 8px);
      margin-left: -8px;
    }
    &:nth-child(5n + 1) {
      width: 100%;
      margin-left: 0;
    }
  }
}

/* ---------- floating contact button ---------- */

.floating-cta {
  position: fixed;
  right: 36px;
  bottom: 30px;
  z-index: 50;
  display: block;
  width: 165px;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateY(35px) scale(0.96);
  transition: opacity 0.4s ease, visibility 0s linear 0.4s,
    transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);

  &.is-visible {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0) scale(1);
    transition-delay: 0s;

    &:hover,
    &:focus-visible {
      transform: translateY(-6px);
    }
  }
  &:active {
    transform: translateY(-2px) scale(0.97);
  }

  img {
    display: block;
    width: 100%;
    height: auto;
  }
}

/* Front (pill) and back ("문의하기") flip over every 7s. */
.floating-cta-stage {
  position: relative;
  display: block;
  width: 100%;
  padding-top: 95.63%; /* 402.52 / 420.9, the front artwork's aspect ratio */
  transform-style: preserve-3d;
  animation: cta-flip 7s cubic-bezier(0.65, 0, 0.35, 1) infinite;
}

.floating-cta-face {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 20px;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.floating-cta-back {
  transform: rotateX(180deg);
}

@keyframes cta-flip {
  0%,
  42.85% {
    transform: perspective(900px) rotateX(0deg) scaleY(1);
  }
  50%,
  92.85% {
    transform: perspective(900px) rotateX(-180deg) scaleY(0.98);
  }
  100% {
    transform: perspective(900px) rotateX(-360deg) scaleY(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .floating-cta-stage {
    animation: none;
  }
  .floating-cta-back {
    display: none;
  }
}

@media (max-width: 1100px) {
  .floating-cta {
    right: 24px;
    bottom: 24px;
  }
}

@media (max-width: 760px) {
  .floating-cta {
    right: 10px;
    bottom: calc(10px + env(safe-area-inset-bottom));
    width: 100px;
  }
}
</style>
